import { IEditorV3, stringToV3 } from "@azu-business/editor-v3";
import { AibBlockLine, AibLineType, AsupInternalBlock, useArsContext } from "aic";
import { AnnotationButton } from "components/annotations/AnnotationButton";
import { v3EditorProps } from "components/common/v3EditorProps";
import { useAppSettings, useHierarchyContext, useTlfContext } from "contexts/AppSettingsContext";
import { getExternalSingles } from "contexts/tlf/getExternalSingles";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { isTlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { isEqual } from "lodash";
import { ReactNode, useMemo } from "react";
import { Col, Container, Row } from "react-bootstrap";

/**
 * Render the 'Page By' editor block for a TLF item, with annotation controls.
 *
 * @returns React node containing the PageBy block or null when context missing.
 */
export const PageBy = (): ReactNode => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const { state: ars } = useArsContext();
  const tlfContext = useTlfContext();
  const tlfData = isTlfItemReadDto(tlfContext.tlfData) ? tlfContext.tlfData : undefined;
  const pageByData = tlfData && !Array.isArray(tlfData.pageBy) ? tlfData.pageBy : undefined;

  const externalSingles = getExternalSingles(
    undefined,
    ars.analysisSets[tlfData?.analysisSetId ?? ""]?.label ?? "",
    tlfData?.releaseStatus,
    tlfData?.domain,
    tlfData?.singles,
    hierarchyContext?.hierarchy?.level,
    hierarchyContext?.hierarchy?.singles,
    hierarchyContext?.hierarchy?.domainReplacements,
    hierarchyContext?.parentTemplates.singles,
    hierarchyContext?.parentTemplates.domainReplacements,
  );

  const pageBy: AibBlockLine<IEditorV3>[] = useMemo(() => {
    return [
      {
        aifid: "page-by",
        left: pageByData?.content ?? stringToV3(""),
        lineType: AibLineType.leftOnly,
        addBelow: false,
        canChangeType: false,
        canEdit: true,
        canRemove: false,
        canMove: false,
        center: null,
        right: null,
        coreTrafficLight: "BlankNotEvaluated",
      },
    ];
  }, [pageByData]);

  /**
   * Handle updates to the Page By block lines, sending changes to TLF context as an IEditorV3.
   *
   * @param ret - Updated array of AibBlockLine items.
   */
  const setPageBy = (ret: AibBlockLine<IEditorV3>[]) => {
    // Update TLF context if content has changed
    /* istanbul ignore else */
    if (pageByData && !isEqual(ret[0].left, pageByData.content))
      tlfContext.dispatch({
        operation: UPDATE_TLF,
        tlfData: {
          pageBy: {
            content:
              ret[0].left ??
              // istanbul ignore next
              stringToV3(""),
            annotation: pageByData.annotation,
          },
        },
        calledFrom: "PageBy:setLines",
      });
  };

  return !tlfContext || !tlfData || !pageByData ? null : (
    <Container>
      <Row className="mt-4">
        <Col>
          <AnnotationButton
            buttonId="pageby-annotation"
            annotationType="PageBy"
            existingAnnotation={pageByData.annotation}
            hidden={tlfContext.tlfViewMode.value !== "ANNOTATIONS"}
            showPreview={tlfContext.tlfViewMode.value === "ANNOTATIONS"}
          />
          <AsupInternalBlock<IEditorV3>
            id="pageby"
            lineStyle={appSettings.defaultTableStyle}
            lines={pageBy}
            disabled={tlfContext.readOnly}
            onChange={setPageBy}
            externalSingles={externalSingles}
            minLines={1}
            maxLines={1}
            defaultType={AibLineType.leftOnly}
            editorProps={v3EditorProps}
            noTrafficLights
          />
        </Col>
      </Row>
    </Container>
  );
};

PageBy.displayName = "PageBy";
